// CustomerReferralRewardsReport.jsx — "Show Total Referral Amount" drill-down from Total Customers

import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Skeleton,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import SavingsIcon from '@mui/icons-material/Savings';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import ReceiptIcon from '@mui/icons-material/Receipt';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../../constants/constants';
// [CASH-MODE] UPI / Cash filter + indication
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeParam, totalsByModeFromRows } from '../../utils/paymentMode';
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeChip from '../../components/PaymentModeChip';
import CustomerReportShell, {
  EmptyState,
  SectionCard,
  customerNoOf,
  formatINR,
  formatISTDate,
} from '../../components/CustomerReportShell';

// [CASH-MODE] commission rows with the mode of their bill (commission mode wins when present)
const commissionRowsWithMode = (referrals) =>
  (referrals || []).flatMap((ref) =>
    (ref.bills || []).map((bill, i) => ({
      amount: ref.commissions?.[i]?.amount,
      paymentMode: ref.commissions?.[i]?.paymentMode || bill.paymentMode,
    }))
  );

const headCell = { fontWeight: 700, color: '#334155', bgcolor: '#f8fafc', whiteSpace: 'nowrap' };

const ReferralRow = ({ row, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' }, cursor: 'pointer' }} onClick={() => setOpen(!open)}>
        <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>{index}</TableCell>
        <TableCell>
          <Chip label={customerNoOf({ customerNo: row.referredCustomerNo })} size="small" variant="outlined" color="primary" sx={{ fontFamily: 'monospace', fontWeight: 700 }} />
        </TableCell>
        <TableCell sx={{ fontWeight: 700 }}>{row.referredCustomerName}</TableCell>
        <TableCell>{formatISTDate(row.joinDate)}</TableCell>
        <TableCell align="center">{row.count || 0}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 700 }}>{formatINR(row.totalBills)}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 800, color: 'success.dark' }}>{formatINR(row.totalCommission)}</TableCell>
        <TableCell align="center">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen(!open); }} aria-label="Show transactions">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ my: 1.5, mx: { xs: 0, md: 2 }, p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Transactions ({row.count})
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Bill Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Commission</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Mode</TableCell>{/* [CASH-MODE] */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(row.bills || []).map((bill, i) => (
                    <TableRow key={i}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{formatISTDate(bill.date)}</TableCell>
                      <TableCell align="right">{formatINR(bill.amount)}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.dark', fontWeight: 700 }}>{formatINR(row.commissions?.[i]?.amount)}</TableCell>
                      {/* [CASH-MODE] */}
                      <TableCell><PaymentModeChip mode={row.commissions?.[i]?.paymentMode || bill.paymentMode || 'UPI'} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const CustomerReferralRewardsReport = () => {
  const [referrals, setReferrals] = useState([]);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  const [totalsByMode, setTotalsByMode] = useState(null); // [CASH-MODE] API totalsByMode when provided
  const { customerId } = useParams();

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const token = sessionStorage.getItem('token');
      const { data } = await axios.get(`${API_BASE_URL}/referralActivities/referral-summary/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { paymentMode: paymentModeParam(paymentMode) }, // [CASH-MODE] undefined for ALL
      });
      setReferrals(data.referrals || []);
      setTotalEarnings(data.totalEarnings || 0);
      setTotalsByMode(data.totalsByMode || null); // [CASH-MODE]
    } catch (e) {
      console.error(e);
      setError('Error fetching referral summary.');
      setReferrals([]);
      setTotalEarnings(0);
      setTotalsByMode(null);
    }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [customerId, paymentMode]); // [CASH-MODE] refetch on mode change

  // [CASH-MODE] API totals may key the sum as totalEarnings or amount
  const modeTotals = totalsByMode || totalsByModeFromRows(commissionRowsWithMode(referrals), (c) => c.amount);
  const modeAmountKey = modeTotals?.UPI?.totalEarnings !== undefined ? 'totalEarnings' : 'amount';
  const totalBills = referrals.reduce((s, r) => s + (Number(r.totalBills) || 0), 0);
  const totalTxns = referrals.reduce((s, r) => s + (Number(r.count) || 0), 0);

  return (
    <CustomerReportShell
      title="Referral Rewards"
      subtitle="Customers referred by this customer and the commission earned on their bills"
      icon={<GroupAddIcon />}
      customerId={customerId}
      filters={(
        <Box sx={{ maxWidth: 280 }}>
          {/* [CASH-MODE] */}
          <PaymentModeFilter value={paymentMode} onChange={setPaymentMode} fullWidth />
        </Box>
      )}
      error={error}
      onRetry={fetchData}
      statsLoading={loading}
      stats={[
        { label: 'Total Earnings', value: formatINR(totalEarnings), hint: 'Commission till now', color: 'success', icon: <SavingsIcon /> },
        { label: 'Referred Customers', value: referrals.length, hint: `${totalTxns} referral transaction${totalTxns === 1 ? '' : 's'}`, color: 'primary', icon: <GroupAddIcon /> },
        { label: 'Bills by Referrals', value: formatINR(totalBills), hint: 'Total billed', color: 'secondary', icon: <ReceiptIcon /> },
        { label: 'Earnings · UPI', value: formatINR(modeTotals?.UPI?.[modeAmountKey]), hint: `${modeTotals?.UPI?.count || 0} transaction${modeTotals?.UPI?.count === 1 ? '' : 's'}`, color: 'info', icon: <QrCode2Icon /> },
        { label: 'Earnings · Cash', value: formatINR(modeTotals?.CASH?.[modeAmountKey]), hint: `${modeTotals?.CASH?.count || 0} transaction${modeTotals?.CASH?.count === 1 ? '' : 's'}`, color: 'warning', icon: <LocalAtmIcon /> },
      ]}
    >
      <SectionCard title="Referred Customers" subtitle="Click a row to see its transactions">
        {loading ? (
          <Box sx={{ p: 1 }}>
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} height={44} />)}
          </Box>
        ) : referrals.length === 0 ? (
          <EmptyState icon={<GroupAddIcon sx={{ fontSize: 52 }} />} title="No referral data found" message="This customer has not earned referral commission yet." />
        ) : (
          <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
            <Table size="small" sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCell}>#</TableCell>
                  <TableCell sx={headCell}>Customer ID</TableCell>
                  <TableCell sx={headCell}>Customer Name</TableCell>
                  <TableCell sx={headCell}>Join Date</TableCell>
                  <TableCell sx={headCell} align="center">Txns</TableCell>
                  <TableCell sx={headCell} align="right">Total Bills</TableCell>
                  <TableCell sx={headCell} align="right">Commission</TableCell>
                  <TableCell sx={headCell} align="center">Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {referrals.map((r, idx) => (
                  <ReferralRow key={r.referredCustomerId || idx} row={r} index={idx + 1} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>
    </CustomerReportShell>
  );
};

export default CustomerReferralRewardsReport;
